import { useState, useEffect, useCallback } from 'react';
import { useVersionedApi } from '../utils/versionedApi';
import { useAuth } from '../AuthContext';
import { dedupeInflight } from '../utils/inflightDedupe';
import { safeStorage } from '../utils/safeStorage';

// Many components use this hook and mount together (shell, page, modals); they
// share one in-flight request per user and organization. Nothing is cached.
const FEATURES_SCOPE = {};
const NOT_CONFIGURED = Object.freeze({ configured: false });
// Opt-in clinical checks; everything off until the server says otherwise.
const NO_CLINICAL_CHECKS = Object.freeze({});

/**
 * Org-gated feature entitlements for the current organization.
 *
 * AraSync ships some capabilities only to the owner's org (WeCare) or to
 * customers granted an add-on; this hook tells the UI which of those the
 * current org has, so components can show/hide gated affordances. The server
 * still enforces every gated action — this is presentation only.
 *
 * Usage:
 *   const { hasFeature } = useOrgFeatures();
 *   if (hasFeature('ads_overlap_override')) { ... }
 */
export function useOrgFeatures() {
  const apiClient = useVersionedApi('v1');
  const { user, authChecked } = useAuth();
  const [features, setFeatures] = useState([]);
  const [externalIntegrations, setExternalIntegrations] = useState({});
  // How {{provider|...}} tokens in ADS form templates render for this org
  // (see utils/adsProviderWording). null until loaded or when unavailable.
  const [adsFormProvider, setAdsFormProvider] = useState(null);
  // The billing system this org used before AraSync, for "billed externally" labels.
  const [previousBillingSystem, setPreviousBillingSystem] = useState(null);
  // This org's own pay period grid ({ configured: false } until an admin sets it).
  const [payPeriodGrid, setPayPeriodGrid] = useState(NOT_CONFIGURED);
  // Opt-in clinical checks (e.g. assessmentPlanAlignment), read here so a
  // switched-off check is never called just to learn that it is off.
  const [clinicalChecks, setClinicalChecks] = useState(NO_CLINICAL_CHECKS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    // Stay loading until the session check finishes, so a route guard keeps its
    // spinner instead of deciding on a cached user that may not be signed in.
    if (authChecked === false) return () => { alive = false; };
    // Entitlements belong to an organization and tenantMiddleware reads only
    // the Authorization header. A platform admin (organizationId null), a
    // signed-out visitor, or a cached user without a token can only get a
    // 401/400 here, so skip the request and report no features.
    if (!user?.organizationId || !safeStorage.getItem('evv_token')) {
      setFeatures([]);
      setExternalIntegrations({});
      setClinicalChecks(NO_CLINICAL_CHECKS);
      setError(null);
      setLoading(false);
      return () => { alive = false; };
    }
    setLoading(true);
    setError(null);
    const userKey = `${user?.organizationId ?? ''}:${user?.id ?? user?.userId ?? ''}`;
    dedupeInflight(FEATURES_SCOPE, `features:${userKey}`, () => apiClient.get('/organizations/current/features'))
      .then((res) => {
        if (alive) {
          setFeatures(res.data?.features || []);
          setExternalIntegrations(res.data?.externalIntegrations || {});
          setAdsFormProvider(res.data?.adsFormProvider || null);
          setPreviousBillingSystem(res.data?.previousBillingSystem || null);
          setPayPeriodGrid(res.data?.payPeriodGrid?.configured ? res.data.payPeriodGrid : NOT_CONFIGURED);
          setClinicalChecks(res.data?.clinicalChecks && typeof res.data.clinicalChecks === 'object' ? res.data.clinicalChecks : NO_CLINICAL_CHECKS);
          setError(null);
        }
      })
      .catch(() => {
        if (alive) {
          setFeatures([]);
          setExternalIntegrations({});
          setAdsFormProvider(null);
          setClinicalChecks(NO_CLINICAL_CHECKS);
          setError('Organization access settings could not be loaded');
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authChecked]);

  const hasFeature = useCallback((key) => features.includes(key), [features]);
  const hasExternalIntegration = useCallback(
    (key) => externalIntegrations[key] === true,
    [externalIntegrations]
  );

  return { features, externalIntegrations, adsFormProvider, previousBillingSystem, payPeriodGrid, clinicalChecks, hasFeature, hasExternalIntegration, loading, error };
}

export default useOrgFeatures;
